// Products Search Service - Product search via microservice
import { get, post, postFormData } from './algoliaApiClient'

// Base endpoint for product search
const PRODUCTS_SEARCH_ENDPOINT = '/api/v1/products/search'

// Base endpoint for product update
const PRODUCTS_UPDATE_ENDPOINT = '/api/v1/products/update-product'

// Restricción temporal: el backend endurecerá validaciones en update-product
const ALLOWED_PRODUCT_UPDATE_FIELDS = ['DESCRIPCION']

const RESTRICTED_PRODUCT_UPDATE_ERROR =
  'Actualización temporalmente restringida: solo se permite modificar la DESCRIPCION del producto.'

/**
 * Valida que update-product solo reciba DESCRIPCION (medida de seguridad temporal).
 * @param {Object} updateData
 * @returns {{ valid: boolean, error?: string, updates?: Object }}
 */
const validateRestrictedProductUpdate = updateData => {
  const keys = Object.keys(updateData || {})
  const disallowedKeys = keys.filter(key => !ALLOWED_PRODUCT_UPDATE_FIELDS.includes(key))

  if (disallowedKeys.length > 0) {
    return {
      valid: false,
      error: `${RESTRICTED_PRODUCT_UPDATE_ERROR} Campos no permitidos: ${disallowedKeys.join(', ')}.`,
    }
  }

  const descripcion = updateData.DESCRIPCION
  if (typeof descripcion !== 'string' || !descripcion.trim()) {
    return {
      valid: false,
      error: 'La DESCRIPCION del producto es requerida.',
    }
  }

  return {
    valid: true,
    updates: {
      DESCRIPCION: descripcion.trim(),
    },
  }
}

// Default results per page
const DEFAULT_PER_PAGE = 20

/**
 * Search products using the new search endpoint
 * @param {Object} params - Search parameters
 * @param {string} params.q - Search query text (optional)
 * @param {number} params.page - Page number (default: 0)
 * @param {number} params.perPage - Results per page (default: 20)
 * @returns {Promise<Object>} Search results with products array and metadata
 */
export const searchProducts = async ({ q = '', page = 0, perPage = DEFAULT_PER_PAGE }) => {
  try {
    // Build query parameters
    const queryParams = new URLSearchParams()

    if (q && q.trim() !== '') {
      queryParams.append('q', q.trim())
    }

    if (page !== undefined && page !== null) {
      queryParams.append('page', page.toString())
    }

    if (perPage !== undefined && perPage !== null) {
      queryParams.append('perPage', perPage.toString())
    }

    // Build full URL with query parameters
    const url = `${PRODUCTS_SEARCH_ENDPOINT}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    // Make GET request to products search endpoint
    const response = await get(url)

    // Handle response structure: { data: { hits, nbHits, page, nbPages, hitsPerPage, query } }
    if (response && response.data) {
      const data = response.data

      return {
        success: true,
        results: data.hits || [],
        hits: data.hits || [], // Keep both for compatibility
        page: data.page !== undefined ? data.page : page,
        nbPages: data.nbPages !== undefined ? data.nbPages : 0,
        nbPagesTotal: data.nbPages !== undefined ? data.nbPages : 0, // Alias
        totalHits: data.nbHits !== undefined ? data.nbHits : 0,
        nbHits: data.nbHits !== undefined ? data.nbHits : 0, // Alias
        hitsPerPage: data.hitsPerPage !== undefined ? data.hitsPerPage : perPage,
        query: data.query || q || '',
        rawResponse: response, // Keep raw response for debugging
      }
    } else {
      // Unexpected response structure
      console.warn('Unexpected response structure from products search:', response)
      return {
        success: false,
        error: 'Estructura de respuesta inesperada',
        results: [],
        hits: [],
        page: page || 0,
        nbPages: 0,
        nbPagesTotal: 0,
        totalHits: 0,
        nbHits: 0,
        query: q || '',
        rawResponse: response,
      }
    }
  } catch (error) {
    console.error('Error in searchProducts:', error)

    // Handle different error types
    let errorMessage = 'Error al buscar productos'

    if (error.networkError) {
      errorMessage = 'Error de conexión. Verifica tu conexión a internet.'
    } else if (error.timeout) {
      errorMessage = 'La búsqueda tardó demasiado tiempo. Por favor, intenta nuevamente.'
    } else if (error.status) {
      errorMessage = `Error del servidor (${error.status}): ${error.message || 'Error desconocido'}`
    } else if (error.message) {
      errorMessage = error.message
    }

    return {
      success: false,
      error: errorMessage,
      results: [],
      hits: [],
      page: page || 0,
      nbPages: 0,
      nbPagesTotal: 0,
      totalHits: 0,
      nbHits: 0,
      query: q || '',
      originalError: error,
    }
  }
}

/**
 * Search products with pagination
 * @param {Object} params - Search parameters
 * @param {string} params.q - Search query text
 * @param {number} params.page - Page number (0-indexed)
 * @param {number} params.perPage - Results per page
 * @returns {Promise<Object>} Paginated search results
 */
export const searchProductsPaginated = async ({ q = '', page = 0, perPage = DEFAULT_PER_PAGE }) => {
  return searchProducts({ q, page, perPage })
}

/**
 * Update a product
 * @param {string} objectID - Product objectID (CODIGO) to identify the product
 * @param {Object} updateData - Data to update (e.g., { DESCRIPCION: "New name" })
 * @returns {Promise<Object>} Update result with success status and data
 */
export const updateProduct = async (objectID, updateData = {}) => {
  try {
    if (!objectID) {
      return {
        success: false,
        error: 'El objectID del producto es requerido',
      }
    }

    if (!updateData || Object.keys(updateData).length === 0) {
      return {
        success: false,
        error: 'No se proporcionaron datos para actualizar',
      }
    }

    const validation = validateRestrictedProductUpdate(updateData)
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error,
      }
    }

    // Build request payload matching backend format
    // Expected format: { objectID: "xxx", updates: { DESCRIPCION: "xxx" } }
    const payload = {
      objectID: objectID,
      updates: validation.updates,
    }

    // Make POST request to update product endpoint
    const response = await post(PRODUCTS_UPDATE_ENDPOINT, payload)

    // Handle response structure
    if (response) {
      return {
        success: true,
        data: response.data || response,
        message: response.message || 'Producto actualizado correctamente',
      }
    } else {
      return {
        success: false,
        error: 'Respuesta inesperada del servidor',
      }
    }
  } catch (error) {
    console.error('Error in updateProduct:', error)

    // Handle different error types
    let errorMessage = 'Error al actualizar el producto'

    if (error.networkError) {
      errorMessage = 'Error de conexión. Verifica tu conexión a internet.'
    } else if (error.timeout) {
      errorMessage = 'La solicitud tardó demasiado tiempo. Por favor, intenta nuevamente.'
    } else if (error.status) {
      errorMessage = `Error del servidor (${error.status}): ${error.message || 'Error desconocido'}`
    } else if (error.message) {
      errorMessage = error.message
    }

    return {
      success: false,
      error: errorMessage,
      originalError: error,
    }
  }
}

/**
 * Update sections products from Algolia
 * This endpoint synchronizes product data from Algolia to all sections
 * @returns {Promise<Object>} Update result with success status and data
 */
export const updateSectionsProducts = async () => {
  try {
    const endpoint = '/api/v1/products/update-sections-products'

    // Make POST request to update sections products endpoint
    const response = await post(endpoint)

    // Handle response structure
    if (response && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.data.message || 'Actualización de productos completada',
      }
    } else {
      return {
        success: false,
        error: 'Respuesta inesperada del servidor',
      }
    }
  } catch (error) {
    console.error('Error in updateSectionsProducts:', error)

    // Handle different error types
    let errorMessage = 'Error al actualizar productos de las secciones'

    if (error.networkError) {
      errorMessage = 'Error de conexión. Verifica tu conexión a internet.'
    } else if (error.timeout) {
      errorMessage = 'La solicitud tardó demasiado tiempo. Por favor, intenta nuevamente.'
    } else if (error.status) {
      errorMessage = `Error del servidor (${error.status}): ${error.message || 'Error desconocido'}`
    } else if (error.message) {
      errorMessage = error.message
    }

    return {
      success: false,
      error: errorMessage,
      status: error.status,
      originalError: error,
    }
  }
}

const SYNC_BANNERS_PRODUCTS_ENDPOINT = '/api/v1/products/sync-banners-products'
const MAX_CSV_FILE_SIZE_BYTES = 5 * 1024 * 1024

const mapSyncBannersProductsError = error => {
  let errorMessage = 'Error al sincronizar productos del banner desde CSV'

  if (error.networkError) {
    errorMessage = 'Error de conexión. Verifica tu conexión a internet.'
  } else if (error.timeout) {
    errorMessage = 'La solicitud tardó demasiado tiempo. Por favor, intenta nuevamente.'
  } else if (error.status === 401) {
    errorMessage = 'Credenciales inválidas para el servicio de Algolia.'
  } else if (error.status === 403) {
    errorMessage = 'Origen no permitido por la política CORS del servicio de Algolia.'
  } else if (error.status === 400) {
    errorMessage = error.message || 'Solicitud inválida. Verifica el archivo CSV.'
  } else if (error.message) {
    errorMessage = error.message
  }

  return {
    success: false,
    error: errorMessage,
    status: error.status,
    data: error.data?.data || null,
    originalError: error,
  }
}

/**
 * Import banner products from CSV (replaces all products for the banner).
 * @param {Object} params
 * @param {string} params.idBanner - Banner ID in Firebase
 * @param {File} params.csvFile - CSV file selected by the user
 * @param {boolean} [params.dryRun=false] - Simulate without writing to Firebase
 * @returns {Promise<Object>} Sync result with statistics and row errors
 */
export const syncBannersProductsFromCsv = async ({ idBanner, csvFile, dryRun = false }) => {
  try {
    const trimmedBannerId = idBanner?.trim()

    if (!trimmedBannerId) {
      return {
        success: false,
        error: 'El ID del banner es requerido',
      }
    }

    if (!csvFile) {
      return {
        success: false,
        error: 'Debes seleccionar un archivo CSV',
      }
    }

    if (!csvFile.name?.toLowerCase().endsWith('.csv')) {
      return {
        success: false,
        error: 'El archivo debe tener extensión .csv',
      }
    }

    if (csvFile.size > MAX_CSV_FILE_SIZE_BYTES) {
      return {
        success: false,
        error: 'El archivo CSV excede el tamaño máximo permitido (5 MB)',
      }
    }

    const formData = new FormData()
    formData.append('idBanner', trimmedBannerId)
    formData.append('products', csvFile)

    if (dryRun) {
      formData.append('dryRun', 'true')
    }

    const response = await postFormData(SYNC_BANNERS_PRODUCTS_ENDPOINT, formData)

    if (response?.data) {
      return {
        success: response.data.success === true,
        cancelled: response.data.cancelled === true,
        data: response.data,
        status: 200,
        dryRun: Boolean(dryRun),
      }
    }

    return {
      success: false,
      error: 'Estructura de respuesta inesperada',
      status: 200,
      rawResponse: response,
    }
  } catch (error) {
    console.error('Error in syncBannersProductsFromCsv:', error)

    if (error.status === 422 && error.data?.data) {
      return {
        success: false,
        cancelled: error.data.data.cancelled === true,
        data: error.data.data,
        status: 422,
        error: error.data.data.message || error.data.data.reason || error.message,
        dryRun: Boolean(dryRun),
      }
    }

    return mapSyncBannersProductsError(error)
  }
}

/**
 * Update banners products from Algolia
 * This endpoint synchronizes product data from Algolia to all banners
 * @returns {Promise<Object>} Update result with success status and data
 */
export const updateBannersProducts = async () => {
  try {
    const endpoint = '/api/v1/products/update-banners-products'

    const response = await post(endpoint)

    if (response && response.data) {
      return {
        success: true,
        data: response.data,
        message: response.data.message || 'Actualización de productos completada',
      }
    } else {
      return {
        success: false,
        error: 'Respuesta inesperada del servidor',
      }
    }
  } catch (error) {
    console.error('Error in updateBannersProducts:', error)

    let errorMessage = 'Error al actualizar productos de los banners'

    if (error.networkError) {
      errorMessage = 'Error de conexión. Verifica tu conexión a internet.'
    } else if (error.timeout) {
      errorMessage = 'La solicitud tardó demasiado tiempo. Por favor, intenta nuevamente.'
    } else if (error.status) {
      errorMessage = `Error del servidor (${error.status}): ${error.message || 'Error desconocido'}`
    } else if (error.message) {
      errorMessage = error.message
    }

    return {
      success: false,
      error: errorMessage,
      status: error.status,
      originalError: error,
    }
  }
}

/**
 * Search products by store using the search-by-store endpoint
 * @param {Object} params - Search parameters
 * @param {number} params.store - Store division ID (required)
 * @param {string} params.query - Search query text (optional)
 * @param {number} params.page - Page number (default: 0)
 * @param {number} params.perPage - Results per page (default: 20)
 * @param {string} params.category - Category filter (optional)
 * @param {number} params.offerType - Offer type filter (101, 103, or any, optional)
 * @returns {Promise<Object>} Search results with products array and metadata
 */
export const searchProductsByStore = async ({
  store,
  query = '',
  page = 0,
  perPage = 20,
  category = '',
  offerType = null,
}) => {
  try {
    if (!store) {
      return {
        success: false,
        error: 'El ID de la tienda es requerido',
        results: [],
        hits: [],
        page: page || 0,
        nbPages: 0,
        totalHits: 0,
        query: query || '',
      }
    }

    // Build request payload
    const payload = {
      store: parseInt(store),
      query: query.trim() || '',
      page: page || 0,
      perPage: perPage || 20,
    }

    // Add optional filters
    if (category && category.trim() !== '') {
      payload.category = category.trim()
    }

    if (offerType !== null && offerType !== undefined && offerType !== '') {
      payload.offerType = parseInt(offerType)
    }

    // Make POST request to search-by-store endpoint
    const endpoint = '/api/v1/products/search-by-store'
    const response = await post(endpoint, payload)

    // Handle response structure
    if (response && response.data) {
      const data = response.data

      return {
        success: true,
        results: data.hits || [],
        hits: data.hits || [], // Keep both for compatibility
        page: data.page !== undefined ? data.page : page,
        nbPages: data.nbPages !== undefined ? data.nbPages : 0,
        nbPagesTotal: data.nbPages !== undefined ? data.nbPages : 0,
        totalHits: data.nbHits !== undefined ? data.nbHits : 0,
        nbHits: data.nbHits !== undefined ? data.nbHits : 0,
        hitsPerPage: data.hitsPerPage !== undefined ? data.hitsPerPage : perPage,
        query: query || '',
        rawResponse: response,
      }
    } else {
      console.warn('Unexpected response structure from search-by-store:', response)
      return {
        success: false,
        error: 'Estructura de respuesta inesperada',
        results: [],
        hits: [],
        page: page || 0,
        nbPages: 0,
        totalHits: 0,
        query: query || '',
        rawResponse: response,
      }
    }
  } catch (error) {
    console.error('Error in searchProductsByStore:', error)

    let errorMessage = 'Error al buscar productos por tienda'

    if (error.networkError) {
      errorMessage = 'Error de conexión. Verifica tu conexión a internet.'
    } else if (error.timeout) {
      errorMessage = 'La búsqueda tardó demasiado tiempo. Por favor, intenta nuevamente.'
    } else if (error.status) {
      errorMessage = `Error del servidor (${error.status}): ${error.message || 'Error desconocido'}`
    } else if (error.message) {
      errorMessage = error.message
    }

    return {
      success: false,
      error: errorMessage,
      results: [],
      hits: [],
      page: page || 0,
      nbPages: 0,
      totalHits: 0,
      query: query || '',
      originalError: error,
    }
  }
}

/**
 * Get product by code and store (by-code-store endpoint).
 * Product must exist and be available in the store (EXISTENCIA_store > 0, PRECIO_store > 0).
 * @param {Object} params - Request parameters
 * @param {string} params.code - Product code (5-14 numeric characters)
 * @param {number} params.store - Store division ID (4 digits, 1000-9999)
 * @returns {Promise<Object>} { success, product, found, code, error? }
 */
export const getProductByCodeStore = async ({ code, store }) => {
  try {
    if (!code || String(code).trim() === '') {
      return {
        success: false,
        found: false,
        product: null,
        code: code,
        error: 'El código es obligatorio',
      }
    }
    const storeNum = parseInt(store, 10)
    if (!store || isNaN(storeNum) || storeNum < 1000 || storeNum > 9999) {
      return {
        success: false,
        found: false,
        product: null,
        code,
        error: 'La tienda debe ser un número de 4 dígitos',
      }
    }

    const endpoint = '/api/v1/products/by-code-store'
    const response = await post(endpoint, { code: String(code).trim(), store: storeNum })

    if (response && response.data) {
      const { product, found, code: responseCode } = response.data
      return {
        success: true,
        product: product || null,
        found: !!found,
        code: responseCode || code,
        rawResponse: response,
      }
    }

    return {
      success: false,
      found: false,
      product: null,
      code,
      error: 'Estructura de respuesta inesperada',
      rawResponse: response,
    }
  } catch (error) {
    console.error('Error in getProductByCodeStore:', error)
    const errorMessage = error.networkError
      ? 'Error de conexión. Verifica tu conexión a internet.'
      : error.status
        ? `Error del servidor (${error.status}): ${error.message || 'Error desconocido'}`
        : error.message || 'Error al obtener producto por código y tienda'
    return {
      success: false,
      found: false,
      product: null,
      code,
      error: errorMessage,
      originalError: error,
    }
  }
}

/**
 * Get product by code (all stores) to use as fallback.
 * @param {string} code - Product code
 * @returns {Promise<Object>} { success, product, error? }
 */
export const getProductByCode = async code => {
  try {
    if (!code || String(code).trim() === '') {
      return {
        success: false,
        product: null,
        error: 'El código es obligatorio',
      }
    }

    const endpoint = `/api/v1/products/by-code?code=${encodeURIComponent(String(code).trim())}`
    const response = await get(endpoint)

    if (response && response.data) {
      const { product } = response.data
      if (product) {
        return {
          success: true,
          product,
          rawResponse: response,
        }
      }
      return {
        success: false,
        product: null,
        error: 'Producto no encontrado en Algolia',
        rawResponse: response,
      }
    }

    return {
      success: false,
      product: null,
      error: 'Estructura de respuesta inesperada',
      rawResponse: response,
    }
  } catch (error) {
    console.error('Error in getProductByCode:', error)
    const errorMessage = error.networkError
      ? 'Error de conexión. Verifica tu conexión a internet.'
      : error.status
        ? `Error del servidor (${error.status}): ${error.message || 'Error desconocido'}`
        : error.message || 'Error al obtener producto por código'
    return {
      success: false,
      product: null,
      error: errorMessage,
      originalError: error,
    }
  }
}

export default {
  searchProducts,
  searchProductsPaginated,
  updateProduct,
  updateSectionsProducts,
  updateBannersProducts,
  syncBannersProductsFromCsv,
  searchProductsByStore,
  getProductByCodeStore,
  getProductByCode,
}
